import React from 'react';
import PageHero from '../components/PageHero';
import { Helmet } from 'react-helmet-async';
import CTASection from '../components/CTASection';
import { SERVICE_TMJ } from '../assets/images';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import tmj from '../assets/Hero/tmj-treatment.svg';

const { FiTarget, FiActivity, FiLayers } = FiIcons;

const {
  FiCheck,
  FiWind,
  FiMoon,
  FiMessageCircle,
  FiRefreshCw,
  FiAlertCircle
} = FiIcons;

export default function TMJ() {
  const symptoms = [
    "Jaw pain or tenderness",
    "Clicking or popping sounds",
    "Difficulty chewing",
    "Locking of the jaw joint",
    "Chronic headaches or migraines",
    "Earaches or ringing in ears (Tinnitus)"
  ];

  const symptomIcons = [
    FiWind,        // Mouth breathing
    FiMoon,        // Snoring / sleep issues
    FiMessageCircle, // Speech issues
    FiRefreshCw,   // Tongue thrust / habits
    FiAlertCircle, // TMJ / pain
    FiActivity     // General dysfunction
  ];

  return (
    <div>

      {/* ✅ Helmet Added */}
      <Helmet>
        <title>TMJ Treatment | Jaw Pain & TMJ Disorder Relief</title>
        <meta
          name="description"
          content="Effective TMJ treatment to relieve jaw pain, clicking, headaches, and dysfunction. Advanced airway-focused therapy for long-term relief."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalCondition",
            "name": "Temporomandibular Joint Disorder",
            "alternateName": "TMJ Disorder",
            "description": "A condition affecting the jaw joint causing pain, clicking, and difficulty in movement.",
            "possibleTreatment": [
              {
                "@type": "MedicalTherapy",
                "name": "Myofunctional Therapy"
              },
              {
                "@type": "MedicalDevice",
                "name": "Orthotic Splints"
              }
            ]
          })}
        </script>
      </Helmet>

      <PageHero
        title="TMJ Disorder Treatment"
        subtitle="Comprehensive diagnostics and therapy to relieve jaw pain and restore function."
        image={tmj}
      />

      {/* What is TMJ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-secondary mb-6">Understanding TMJ Disorders</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            The temporomandibular joint (TMJ) connects your jawbone to your skull. When this joint or the surrounding muscles are compromised, it can cause severe discomfort and functional issues. Often, TMJ disorders are closely linked to airway issues and poor sleep posture.
          </p>
        </div>
      </section>

      {/* Symptoms & Causes */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="flex items-center gap-4 mb-8">
              <SafeIcon icon={FiTarget} className="text-3xl text-primary" />
              <h2 className="text-3xl font-bold text-secondary">Common Symptoms</h2>
            </div>

            <ul className="space-y-4">
              {symptoms.map((sym, i) => {
                const Icon = symptomIcons[i] || FiCheck;

                return (
                  <li key={i} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary/10 rounded-full">
                      <SafeIcon icon={Icon} className="text-primary text-sm" />
                    </div>
                    <span className="text-gray-700 font-medium">{sym}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-4 mb-8">
              <SafeIcon icon={FiActivity} className="text-3xl text-primary" />
              <h2 className="text-3xl font-bold text-secondary">Root Causes</h2>
            </div>
            <p className="text-lg text-gray-600 mb-6">
              TMJ disorders can stem from various factors. We focus on identifying the source rather than just masking the pain:
            </p>
            <ul className="space-y-6 text-lg text-gray-700">
              <li className="flex flex-col gap-1 border-l-4 border-primary pl-4">
                <strong className="text-secondary">Airway Compensation</strong>
                <span className="text-base text-gray-600">Pushing the jaw forward to breathe at night strains the joint.</span>
              </li>
              <li className="flex flex-col gap-1 border-l-4 border-primary pl-4">
                <strong className="text-secondary">Bruxism (Teeth Grinding)</strong>
                <span className="text-base text-gray-600">Excessive pressure on the joint, often related to stress or sleep apnea.</span>
              </li>
              <li className="flex flex-col gap-1 border-l-4 border-primary pl-4">
                <strong className="text-secondary">Structural Misalignment</strong>
                <span className="text-base text-gray-600">Improper bite alignment forcing muscles to overwork.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-16 text-center">Our Holistic Treatment Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl">
              <SafeIcon icon={FiLayers} className="text-4xl text-accent2 mb-6" />
              <h3 className="text-xl font-bold text-secondary mb-3">Orthotic Splints</h3>
              <p className="text-gray-600">Customized oral appliances that decompress the joint and provide proper positioning for the jaw to heal.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <SafeIcon icon={FiActivity} className="text-4xl text-accent2 mb-6" />
              <h3 className="text-xl font-bold text-secondary mb-3">Airway Optimization</h3>
              <p className="text-gray-600">Addressing underlying sleep breathing disorders that force the jaw into unnatural, straining positions at night.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <SafeIcon icon={FiTarget} className="text-4xl text-accent2 mb-6" />
              <h3 className="text-xl font-bold text-secondary mb-3">Myofunctional Therapy</h3>
              <p className="text-gray-600">Retraining the oral and facial muscles to function harmoniously, reducing strain on the temporomandibular joint.</p>
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Stop Living with Jaw Pain" subtitle="Schedule an evaluation to uncover the root cause of your TMJ discomfort." />
    </div>
  );
}